// Have the courage to follow your heart and intuition.
// CSS is built separately by Tailwind CLI (npm run build:css)

// Shim: make DOMContentLoaded callbacks fire immediately when the document
// is already interactive (scripts injected by HTMX swaps run after DOMContentLoaded
// has long since fired, so new listeners would never trigger without this).
(function() {
    var _orig = document.addEventListener;
    document.addEventListener = function(type, fn, opts) {
        if (type === 'DOMContentLoaded' && document.readyState !== 'loading') {
            fn();
            return;
        }
        return _orig.call(this, type, fn, opts);
    };
})();

// htmx 2.x ships an ESM build (`dist/htmx.esm.js`) whose only exposure is
// `export default htmx`. A bare side-effect import therefore evaluates the
// module — which is enough for htmx to register its own DOMContentLoaded
// hook and wire up hx-* attributes on the INITIAL server-rendered page —
// but `window.htmx` stays `undefined`. That's a problem for anything that
// needs to re-run htmx on dynamically-injected DOM, e.g. the CODA list
// poller in `pages/coda-upload.js` which swaps `#coda-list` innerHTML and
// then needs `htmx.process(listEl)` to re-wire the new re-parse forms.
// Without this global, those forms stay un-htmx'd, the user clicks
// "Re-parse", the browser performs a native form submit, and they land on
// a bare list fragment with no base template and no CSS (the original
// "naked table" bug). So: import the default and pin it to window.
import htmx from "htmx.org";
window.htmx = htmx;
// Flowbite component classes are imported by ./flowbite-reinit so they can
// be tree-shaken alongside the widget init logic. We intentionally do NOT
// import Flowbite's stock `initFlowbite` anywhere — this project's contract
// is that all widget init goes through `reinitFlowbiteWidgets`.
import { Dropdown, Tooltip, Modal } from "flowbite";
import { reinitFlowbiteWidgets } from "./utils/ui/flowbite-reinit";
import "./components/sidebar";

// HTMX CSRF: attach Django CSRF token to every HTMX request
// Also strip empty query parameters for clean URLs
document.addEventListener('htmx:configRequest', function(evt) {
    var csrfEl = document.querySelector('[name=csrfmiddlewaretoken]');
    if (!csrfEl) {
        var cookies = document.cookie.split(';');
        for (var i = 0; i < cookies.length; i++) {
            var c = cookies[i].trim();
            if (c.startsWith('csrftoken=')) {
                evt.detail.headers['X-CSRFToken'] = c.substring(10);
                break;
            }
        }
    } else {
        evt.detail.headers['X-CSRFToken'] = csrfEl.value;
    }

    if (evt.detail.verb === 'get') {
        var params = evt.detail.parameters;
        Object.keys(params).forEach(function(key) {
            if (params[key] === '' || params[key] === null || params[key] === undefined) {
                delete params[key];
            }
        });
    }
});

// Import utilities (these also expose themselves globally for backward compatibility)
import { initEmailValidation } from "./utils/forms/form-validation.js";
import { initDecimalInputs } from "./utils/forms/decimal-input.js";
import { sanitizeMoneyField } from "./utils/forms/money.js";
import { reapplyItiStyling } from "./utils/forms/phone-input.js";
import { initFormUrlSync } from "./utils/forms/form-url-sync.js";
import { initConfirmAction } from "./utils/ui/confirm-action.js";
import { initConfirmModal } from "./utils/ui/confirm-modal.js";
import { initDeclarativeAttrs } from "./utils/declarative-attrs.js";
import { hydrateCustomerPickerI18n } from "./utils/ui/customer-picker.js";
import { initCustomSelects } from "./components/custom-select.js";
import { bootstrapRealtime } from "./utils/realtime.js";
import * as utils from "./utils/index.js";
import * as components from "./components/index.js";
import * as pages from "./pages/index.js";

// Expose namespaces globally
window.App = window.App || {};
window.App.utils = utils;
window.App.components = components;
window.App.pages = pages;

// Expose Flowbite component classes for dynamic content that needs to
// instantiate a specific widget programmatically (see e.g. components/Modal.js).
// The widget *init* entry point is `window.reinitFlowbiteWidgets`, exposed
// by ./flowbite-reinit. There is deliberately no `window.initFlowbite`.
window.Dropdown = Dropdown;
window.Tooltip = Tooltip;
window.Modal = Modal;

// ── HTMX boosted-navigation lifecycle ──────────────────────────────
// Pages can push teardown callbacks to this array; they run before a
// boosted swap replaces #main-content.
window._pageCleanup = [];

function _runPageCleanup() {
    if (window._pageCleanup && window._pageCleanup.length) {
        window._pageCleanup.forEach(function(fn) { try { fn(); } catch(e) { /* swallow */ } });
    }
    window._pageCleanup = [];
}

// Run page cleanup on browser Back/Forward.
//
// Why this is needed (parallel path to htmx:beforeSwap, not redundant):
//
// htmx:beforeSwap is the cleanup hook for boosted navigation
// (handleAjaxResponse → triggerEvent('htmx:beforeSwap') at htmx.js:4885).
// On Back/Forward HTMX takes a different code path — onpopstate at
// htmx.js:5136 calls restoreHistory(), which calls swap() directly.
// swap() fires htmx:afterSwap but NOT htmx:beforeSwap, so our
// _pageCleanup callbacks never run on popstate.
//
// htmx:beforeHistorySave is also unusable here: HTMX only fires it
// when hx-history="false" is absent (htmx.js:3264-3266), and we
// deliberately pin that attribute on #main-content while Schedule
// is the live page (see _setScheduleHistoryGuard). So the event
// is suppressed exactly when we'd want it.
//
// Without this listener the FullCalendar instance leaks across
// popstate transitions: the JS reference (_calendar) stays
// truthy in module scope, the 60s flag-tick interval keeps
// polling _calendar.getEvents() on a detached DOM tree, and the
// pageshow / visibilitychange listeners keep calling
// _calendar.refetchEvents() against an orphaned widget. The
// defensive `if (_calendar) destroyScheduleCalendar()` guard at
// the top of initScheduleCalendar() does prevent the visible
// "two stacked calendars" symptom on the next visit, but the
// orphan still wastes CPU and timers in the meantime.
//
// `capture: true` so we run BEFORE HTMX's window.onpopstate
// property handler (which has no capture) — guarantees cleanup
// fires while the outgoing #main-content DOM is still mounted.
//
// We MUST gate on the same condition HTMX uses to decide whether
// to swap (htmx.js:5137: `if (event.state && event.state.htmx)`).
// Otherwise we tear down the live page on popstate events HTMX
// will ignore — turning the page blank.
//
// Concrete case this avoids: schedule-calendar.js's applyFilters()
// keeps the URL in sync via raw `window.history.pushState({}, ...)`
// when the user picks a filter (the FC stays mounted, we just
// refetchEvents). The state object has no `htmx: true` flag.
// On Back / Forward across that filter change:
//   - Back to the original /office/schedule/ entry has state
//     {htmx: true} — HTMX swaps, we cleanup, re-init. OK.
//   - Forward to /office/schedule/?cleaner=5 has state {} — HTMX
//     skips. Without this gate we'd cleanup anyway, leaving the
//     user on an empty #main-content with no FC to look at.
//
// Idempotent: _runPageCleanup empties window._pageCleanup after
// running, so any subsequent listener (e.g. an unrelated
// htmx:beforeSwap firing during the same tick) is a no-op.
window.addEventListener('popstate', function(event) {
    if (event.state && event.state.htmx) {
        _runPageCleanup();
    }
}, true);

function _runAutoInit(root, forceReinit) {
    (root || document).querySelectorAll('[data-auto-init]').forEach(function(el) {
        if (el.dataset.autoInitDone && !forceReinit) return;
        el.dataset.autoInitDone = '1';
        var name = el.dataset.autoInit;
        var fnName = 'autoInit' + name.charAt(0).toUpperCase() + name.slice(1);
        if (App.pages && typeof App.pages[fnName] === 'function') {
            App.pages[fnName]();
        }
    });
}

// Soft-reload: re-fetch current page and swap #main-content without a full
// page reload. Falls back to hard reload if HTMX isn't available.
window._softReload = function() {
    if (typeof htmx !== 'undefined' && document.getElementById('main-content')) {
        htmx.ajax('GET', window.location.href, {
            target: '#main-content',
            swap: 'outerHTML',
            select: '#main-content'
        });
    } else {
        window.location.reload();
    }
};

// Language switcher: keep `next` in sync with the current URL after HTMX navigation
document.addEventListener('submit', function(e) {
    var form = e.target;
    if (form.action && form.action.indexOf('/i18n/setlang') !== -1) {
        var nextInput = form.querySelector('input[name="next"]');
        if (nextInput) nextInput.value = window.location.pathname;
    }
});

// Confirm: block boosted navigation when a form has unsaved changes. Pages
// surface their localized copy via ``window._formDirtyConfig`` (rendered from
// {% trans %} data-attrs) and the live state via ``window._formDirty``.
document.body.addEventListener('htmx:confirm', function(e) {
    if (typeof window._formDirty !== 'function' || !window._formDirty()) return;
    e.preventDefault();
    var cfg = window._formDirtyConfig || {};
    var proceed = function() { e.detail.issueRequest(); };
    if (typeof window.showConfirmModal === 'function') {
        window.showConfirmModal({
            title: cfg.title || 'Leave this page?',
            message: cfg.message || 'You have unsaved changes that will be lost.',
            confirmText: cfg.confirmText || 'Leave page',
            cancelText: cfg.cancelText || 'Stay here',
            type: 'warning',
            onConfirm: proceed,
        });
    } else if (confirm(cfg.message || 'You have unsaved changes. Are you sure you want to leave?')) {
        proceed();
    }
});

// Mobile: close the sidebar drawer smoothly while the request is in flight,
// so the OOB swap doesn't abruptly replace a visible drawer.
document.addEventListener('htmx:beforeRequest', function(e) {
    if (window.matchMedia("(min-width: 1024px)").matches) return;
    var elt = e.detail.elt;
    if (!elt || !elt.closest || !elt.closest('#sidebar')) return;

    var sidebar = document.getElementById('sidebar');
    if (!sidebar || sidebar.classList.contains('-translate-x-full')) return;

    sidebar.classList.add('-translate-x-full');
    sidebar.classList.remove('transform-none');
    sidebar.setAttribute('aria-hidden', 'true');

    var backdrop = document.querySelector('[drawer-backdrop]');
    if (backdrop) {
        backdrop.style.transition = 'opacity 0.3s';
        backdrop.style.opacity = '0';
        setTimeout(function() { backdrop.remove(); }, 300);
    }
});

// History cache opt-out for stateful pages (HTMX 2.x).
//
// Why an opt-out is needed: the schedule page mounts FullCalendar
// AND moves server-rendered DOM (``#filter-btn-container``, the
// Calendar/Team ``<nav>``) into the FC toolbar then deletes the
// canonical containers (``#schedule-view-tabs-container``,
// ``#filter-btn-container``). HTMX takes its history snapshot of
// ``#main-content`` AFTER our htmx:beforeSwap cleanup has run
// (saveCurrentPageToHistory is called from line 4909 of htmx.js,
// which is INSIDE the same handleAjaxResponse path that already
// fired beforeSwap — but the destroy happens inside the listener
// we registered, while the snapshot serializes the post-cleanup
// DOM). Either way the cached HTML doesn't match what init wants
// to find on restore: Preact-style diff inside FullCalendar can't
// gracefully replace a stale ``.fc`` tree it didn't render, and
// the move queries can't find canonical containers that were
// removed when init ran on departure. End result: image-a142dd44
// (calendar shows, filter button + tabs missing) and
// image-745f786c (entire FC toolbar collapsed away).
//
// Why ``e.preventDefault()`` on ``htmx:beforeHistorySave`` does NOT
// work in HTMX 2.x: the source unconditionally calls
// ``saveToHistoryCache`` immediately after the trigger
// (htmx.js:3266-3267) — there is no defaultPrevented check. The
// ONLY documented opt-out is the ``hx-history="false"`` attribute,
// which HTMX queries with a document-wide selector right BEFORE
// the cache decision (htmx.js:3264).
//
// How we use it: add ``hx-history="false"`` to ``#main-content``
// in our existing ``htmx:beforeSwap`` listener (which runs BEFORE
// the line-4909 saveCurrentPageToHistory call), and strip it in
// ``htmx:afterSwap`` so the NEXT outgoing page caches normally.
// On Back HTMX hits the cache-miss path, fetches a fresh page
// (the view sees ``HX-Request: true`` and returns the Pattern B
// partial), swaps, fires ``htmx:historyRestore`` — and our
// historyRestore handler down below re-mounts FullCalendar
// against the canonical server DOM. Trade-off: one extra HTTP
// round-trip on Back to /office/schedule.
function _isSchedulePath(path) {
    return path && path.indexOf('/office/schedule') === 0;
}

// Pin / unpin ``hx-history="false"`` on #main-content based on the
// live URL. Idea: as long as the schedule page IS the live page,
// the attribute stays in the document; the moment the user lands
// elsewhere it's stripped.
//
// Why pinned (not toggled at navigation boundaries): HTMX queries
// the document for ``[hx-history="false"]`` from inside
// ``saveCurrentPageToHistory`` (htmx.js:3264), and that function
// runs from TWO different code paths that don't share a listener:
//
//   1. boost-away (handleAjaxResponse:4909) — runs INSIDE htmx:
//      beforeSwap → afterSwap. Our listeners can intercept.
//   2. browser Back/Forward (restoreHistory:3343) — runs FIRST,
//      BEFORE any swap or event our listeners hook. There is no
//      ``htmx:beforeRestoreHistory`` event we can wedge into.
//
// So the only reliable way to make path-(2) skip the cache for
// Schedule is to have the attribute already on #main-content
// while Schedule is the visible page. We set it whenever any
// navigation lands on Schedule and clear it whenever any
// navigation lands somewhere else.
//
// Bug this fixes (FC-DEBUG console trace 2026-05-12):
//   1. Schedule → Customers (boost). beforeSwap set hx-history.
//      saveCurrentPageToHistory skips. After swap, our afterSwap
//      stripped hx-history. Customers loaded.
//   2. Back → Schedule. CACHE MISS (correctly). loadHistory-
//      FromServer. swap() — does NOT fire htmx:beforeSwap, so
//      our beforeSwap listener never runs and hx-history is
//      NEVER re-set on #main-content. Schedule loads, FC1 mounts.
//   3. Forward → Customers. restoreHistory:
//      saveCurrentPageToHistory queries the doc → no
//      [hx-history="false"] → SAVES Schedule under
//      /office/schedule, with the live FC1 mounted DOM in the
//      snapshot. Customers cache hit, restored.
//   4. Back → Schedule. CACHE HIT this time on the poisoned
//      snapshot. swap restores cached HTML containing FC1's
//      frozen subtree. afterSwap re-imports schedule-calendar →
//      init mounts FC2 next to FC1's frozen DOM (Preact treats
//      unknown subtree as out-of-tree and appends instead of
//      replacing). Two stacked calendars, second one missing the
//      moves whose canonical containers were removed in the
//      first init's lifecycle — image-4ee9668f.
function _setScheduleHistoryGuard() {
    var mc = document.getElementById('main-content');
    if (!mc) return;
    if (_isSchedulePath(window.location.pathname)) {
        mc.setAttribute('hx-history', 'false');
    } else {
        mc.removeAttribute('hx-history');
    }
}

// Purge any /office/schedule entries from the htmx history cache.
// ``hx-history="false"`` only prevents NEW saves; it does NOT scrub
// entries already in sessionStorage from a previous session (or from
// a build that pre-dates this opt-out). On Back/Forward HTMX hits
// those stale entries first, restores the frozen FC tree they hold,
// and our fresh init then mounts a SECOND FullCalendar inside the
// same #calendar (Preact treats the stale subtree as out-of-tree
// and appends rather than replaces) — image-4ee9668f / image-
// 4fed84df. Run this on DOMContentLoaded so any leftovers from
// before this code shipped are gone before the user can navigate.
function _purgeScheduleHistoryCache() {
    try {
        var raw = sessionStorage.getItem('htmx-history-cache');
        if (!raw) return;
        var cache = JSON.parse(raw);
        if (!Array.isArray(cache)) return;
        var filtered = cache.filter(function(it) {
            return !(it && typeof it.url === 'string' && _isSchedulePath(it.url));
        });
        if (filtered.length === cache.length) return;
        sessionStorage.setItem('htmx-history-cache', JSON.stringify(filtered));
    } catch (ex) { /* sessionStorage disabled or quota — ignore */ }
}

// Same-URL boost guard. When the user clicks a sidebar entry that points
// at the page they're already on (the obvious example: clicking
// "Planning" while already viewing /office/schedule/?view=calendar) HTMX
// still fires a full request, runs ``htmx:beforeSwap`` (so the cleanup
// destroys the live FullCalendar / Leaflet / TipTap), then performs an
// innerHTML swap of ``#main-content`` and re-mounts the same module on
// the freshly-rendered partial. With ``transition:true`` (Pattern B
// default) this happens INSIDE a View Transition snapshot — the
// browser captures both the old and new state of ``#main-content`` and
// cross-fades, but on same-URL the two snapshots target identical
// view-transition-name groups (root + #main-content). On Chromium
// today this leaves the freshly-mounted FullCalendar's
// ``.fc-toolbar`` flex layout collapsed (toolbar chunks stacked
// vertically — see image-fd9f926a). Same root cause would hit any
// page with a heavy stateful library on same-URL boost.
//
// Treating this click as "user wants to refresh the page" is also the
// least surprising behaviour. We bypass the boost entirely and let the
// browser do a normal full-page navigation, which:
//   - guarantees a clean process for FullCalendar / Leaflet / etc.,
//   - matches what F5 would do,
//   - skips the lossy View Transition snapshot for an unchanged URL.
//
// We also extend the guard to "schedule re-entry" — any click whose
// destination AND current URL are both /office/schedule/ paths, even
// when the search strings differ. Why: schedule-calendar.js's
// applyFilters() sync-pushes /office/schedule/?cleaner=5 via a raw
// pushState (the FC stays mounted, only the URL changes). Clicking
// the sidebar Planning link from there boosts to /office/schedule/ —
// different search, but the SAME view-transition-name group on
// #main-content is captured, with the OLD FC toolbar (filter btn +
// view tabs already moved INTO the toolbar) snapshotted alongside
// the NEW one (canonical containers freshly server-rendered, before
// the move-into-toolbar code has a chance to run). The cross-fade
// strands the new FC's toolbar in its empty-chunk state — exactly
// the collapsed-flex layout in image-70a71327. Forcing a full
// reload skips the View Transition snapshot for this case too.
//
// ``htmx:beforeRequest`` fires before the XHR is even sent, so calling
// ``preventDefault`` here cancels the boost cleanly and the anchor's
// default behaviour (full navigation) takes over.
document.addEventListener('htmx:beforeRequest', function(e) {
    var elt = e.detail && e.detail.elt;
    if (!elt || elt.tagName !== 'A' || !elt.href) return;
    // Ignore links that explicitly opted out (``hx-boost="false"``) —
    // they wouldn't have triggered an htmx request in the first place,
    // but if a future template forces htmx.ajax(...) on a same-URL
    // path we still want it to go through.
    if (elt.getAttribute('hx-boost') === 'false') return;
    var clicked = new URL(elt.href, window.location.origin);
    var samePathname = clicked.pathname === window.location.pathname;
    var sameSearch = clicked.search === window.location.search;
    if (samePathname && (sameSearch || _isSchedulePath(clicked.pathname))) {
        e.preventDefault();
        // Use ``window.location.assign`` instead of ``location.reload``
        // so the new request is logged in browser history as a fresh
        // navigation (matches what an unboosted same-URL anchor would
        // do natively).
        window.location.assign(elt.href);
    }
});

// Before swap: tear down outgoing page + redirect to login if session expired
// Also force full page load for pages that need fresh JS init (schedule/calendar).
document.addEventListener('htmx:beforeSwap', function(e) {
    var tid = e.detail.target && e.detail.target.id;
    if (tid === 'main-content' || tid === 'platform-content') {
        _runPageCleanup();
        document.querySelectorAll('.custom-select-dropdown').forEach(function(d) { d.remove(); });
        document.querySelectorAll('body > [role="tooltip"], body > [modal-backdrop]').forEach(function(el) { el.remove(); });
        // Defensive: re-pin the schedule history guard. It was already
        // set in afterSwap when we landed on Schedule, but a script
        // somewhere may have stripped it. Idempotent set/clear keyed
        // on the OUTGOING URL (location.pathname is still the old URL
        // at this point — pushState hasn't fired yet, see htmx.js:4960
        // beforeSwapCallback). saveCurrentPageToHistory runs right
        // after this listener returns and queries the doc.
        _setScheduleHistoryGuard();
        if (_isSchedulePath(window.location.pathname)) {
            _purgeScheduleHistoryCache();
        }
    }
    // Destroy Leaflet map before its container is swapped to prevent marker leaks.
    var swapTarget = e.detail.target;
    var hasMap = swapTarget && (
        swapTarget.id === 'dash-map' ||
        (swapTarget.querySelector && swapTarget.querySelector('#dash-map'))
    );
    if (hasMap || (swapTarget && swapTarget.id === 'main-content')) {
        if (typeof window._destroyDashMap === 'function') {
            window._destroyDashMap();
        } else if (window._dashMapInstance) {
            try { window._dashMapInstance.remove(); } catch(ex) { /* ignore */ }
            window._dashMapInstance = null;
        }
        var dashMap = document.getElementById('dash-map');
        if (dashMap && dashMap._leaflet_id) {
            delete dashMap._leaflet_id;
        }
        document.querySelectorAll('.leaflet-pane, .leaflet-control-container, .leaflet-marker-icon, .leaflet-marker-shadow').forEach(function(el) {
            if (!el.closest('#dash-map')) el.remove();
        });
    }
    var xhr = e.detail.xhr;
    // Historical force-reload guard for /office/schedule was REMOVED in
    // step 2.d of the Pattern B migration. The schedule_hub.html
    // template now extends ``base_office_template`` (Pattern B partial
    // when HX-Request, full page otherwise), schedule-calendar.js owns
    // an idempotent init + cleanup pair wired into ``_pageCleanup``,
    // schedule-team.js does the same, and the in-page Calendar/Team
    // tabs carry ``hx-boost="false"`` to avoid the swap-into-self
    // layout bug. There is no longer a reason to bypass HTMX swapping
    // when navigating to or away from /office/schedule.
    if (xhr && xhr.responseURL && xhr.responseURL.indexOf('/login') !== -1) {
        window.location.href = xhr.responseURL;
        e.detail.shouldSwap = false;
    }
});

// OOB swaps (e.g. `hx-select-oob="#sidebar"` which re-renders the sidebar
// on every boosted navigation) fire a dedicated `htmx:oobAfterSwap` event
// on the OOB element. HTMX also fires `htmx:afterSwap` a second time for
// the same OOB element (see settleInfo.elts loop in htmx.js), but our
// #main-content handler below filters those out — leaving the sidebar's
// Collapse buttons (Cleaners, Billing, Stock, Emails) to be re-wired here.
// Without this handler the fresh sidebar would silently stop responding to
// clicks after the first boosted nav.
document.addEventListener('htmx:oobAfterSwap', function(evt) {
    var target = evt.detail.elt || evt.target;
    if (target && target.querySelectorAll) {
        reinitFlowbiteWidgets(target);
    }
});

// After swap: re-initialise Flowbite, auto-init forms, email validation
document.addEventListener('htmx:afterSwap', function(evt) {
    // `htmx:afterSwap` fires ONCE PER ELEMENT in settleInfo.elts — which
    // includes the main swap target AND every `hx-select-oob` target (for
    // this layout: `#sidebar`). `evt.detail.target` is always the MAIN
    // swap target (it comes from the shared swapOptions.eventInfo), so if
    // we branched on `detail.target.id` we'd run the #main-content logic
    // twice per boosted navigation: once for the real main swap, and once
    // more when the sidebar OOB fires the same event. That silently
    // re-ran every `data-auto-init` hook and attached duplicate click
    // listeners — toggle widgets like the "Import from template" dropdown
    // on the service-type form ended up with two listeners that cancelled
    // each other out (toggle twice = no-op, looks like a dead button).
    // `evt.target` is the element HTMX actually dispatched on, so use it
    // to tell "main swap" apart from "OOB swap".
    var target = evt.target;
    var id = target && target.id;
    // With `hx-swap="outerHTML"` HTMX briefly references a detached old
    // node on some codepaths. Resolve the live element by id so any
    // subsequent querySelectorAll / scroll manipulation hits the DOM the
    // user actually sees.
    var liveTarget = (id && document.getElementById(id)) || target;

    // Re-pin the schedule history guard against the URL we just
    // landed on. ``location.pathname`` at this point reflects the
    // POST-swap URL (pushState ran inside swap()'s
    // beforeSwapCallback at htmx.js:4960). Set the attribute when
    // schedule becomes the live page — including via cache-miss
    // history restore from loadHistoryFromServer, which calls
    // swap() WITHOUT firing htmx:beforeSwap, so this is the only
    // listener guaranteed to run on every transition into Schedule.
    if (id === 'main-content' || id === 'platform-content') {
        _setScheduleHistoryGuard();
    }

    // Skip re-init entirely for swaps that happen INSIDE an already-open
    // Flowbite panel (e.g. the notification list refreshes itself via
    // htmx:load) — re-wiring would destroy the panel's Popper mid-use.
    if (id !== 'notification-content') {
        reinitFlowbiteWidgets(liveTarget);
    }

    if (id === 'main-content' || id === 'platform-content') {
        var container = liveTarget;
        if (container) container.scrollTop = 0;
        _runAutoInit(container, true);
        // Re-scan the freshly-injected subtree for declarative attrs
        // that need direct binding (data-stop-propagation). Body-level
        // delegates are bound once on DOMContentLoaded and survive
        // every swap on their own.
        initDeclarativeAttrs(container);
    }

    if (id === 'main-content' || id === 'platform-content') {
        initEmailValidation();
        initCustomSelects();

        var hasReportContent = document.getElementById('report-content');
        if (hasReportContent) {
            if (typeof window.ApexCharts !== 'undefined' && typeof window.initReports === 'function') {
                window.initReports();
            } else {
                import(/* webpackChunkName: "reports-charts" */ "./pages/reports-charts");
            }
        }

        // WYSIWYG (TipTap) — mirror the DOMContentLoaded gate below.
        // Without this, navigating to a page that contains an editor
        // (emails/reply, composeModal, email-preview-pane) via Pattern B
        // boost lands the host element but never mounts TipTap, so the
        // textarea stays empty and unwired. The module is idempotent —
        // ``initWysiwyg`` destroys an existing instance first — so it's
        // safe to call on every relevant swap.
        if (
            document.getElementById('wysiwyg-example')
            || document.getElementById('composeModal')
            || document.getElementById('email-preview-pane')
        ) {
            if (typeof window.initWysiwyg === 'function') {
                window.initWysiwyg();
            } else {
                import(/* webpackChunkName: "wysiwyg" */ './utils/ui/wysiwyg');
            }
        }
    }

    // CRITICAL: lazy-import re-mount branches (dashboard-map and
    // schedule-calendar) MUST live inside the main-swap guard. Without
    // it htmx:afterSwap fires once per element in settleInfo.elts —
    // which on every boosted nav includes both #main-content AND the
    // #sidebar OOB. The schedule-calendar branch in particular
    // mutates the swapped DOM (moves <nav> into the FC toolbar,
    // removes #schedule-view-tabs-container), so a second call on
    // the same swap would defensively destroy the freshly-built FC,
    // try to re-locate the (now removed) view-tabs container and
    // silently lose the segmented control. Same root cause as the
    // duplicate-listener bug called out around line 245 above.
    if (id === 'main-content' || id === 'platform-content') {
        if (document.getElementById('dash-map')) {
            import(/* webpackChunkName: "dashboard-map" */ "./pages/dashboard-map").then(function() {
                if (typeof window.initDashboardMap === 'function') window.initDashboardMap();
            });
        }

        // Schedule calendar lazy re-mount on boosted re-entry.
        // ``destroyScheduleCalendar`` runs from ``window._pageCleanup``
        // BEFORE the swap, so the freshly-injected ``#calendar``
        // element is empty when we get here. The module exposes an
        // idempotent ``autoInitScheduleCalendar`` that builds a new
        // FullCalendar instance + registers the next cleanup hook.
        if (document.getElementById('calendar') && window.location.pathname.startsWith('/office/schedule')) {
            import(/* webpackChunkName: "schedule-calendar" */ "./pages/schedule-calendar").then(function(mod) {
                if (typeof mod.autoInitScheduleCalendar === 'function') {
                    mod.autoInitScheduleCalendar();
                }
            });
        }

        // Team resource-timeline lazy re-mount on boosted re-entry.
        // Same contract as the Calendar branch above: ``destroyScheduleTeam``
        // runs from ``window._pageCleanup`` before the swap, so the fresh
        // ``#team-calendar`` is empty when ``autoInitScheduleTeam`` rebuilds it.
        if (document.getElementById('team-calendar') && window.location.pathname.startsWith('/office/schedule')) {
            import(/* webpackChunkName: "schedule-team" */ "./pages/schedule-team").then(function(mod) {
                if (typeof mod.autoInitScheduleTeam === 'function') {
                    mod.autoInitScheduleTeam();
                }
            });
        }

        // Absences resource-timeline lazy re-mount on boosted re-entry
        // (clicking the "Absences" sidebar entry). Same contract as the
        // schedule branches: ``destroyAbsencesTimeline`` runs from
        // ``window._pageCleanup`` before the swap, and the module also wipes
        // the ``#abs-calendar`` mount defensively, so re-init builds a single
        // clean FullCalendar. The mount only exists in calendar view (the
        // template skips it in list view), so the element check doubles as a
        // view gate.
        if (document.getElementById('abs-calendar') && window.location.pathname.startsWith('/office/cleaners/absences')) {
            import(/* webpackChunkName: "absences-timeline" */ "./pages/absences-timeline").then(function(mod) {
                if (typeof mod.autoInitAbsencesTimeline === 'function') {
                    mod.autoInitAbsencesTimeline();
                }
            });
        }
    }
});

// (Intentionally no historyRestore re-init handler.)
//
// In HTMX 2.x BOTH the cache-hit path (``restoreHistory``) and the
// cache-miss path (``loadHistoryFromServer``) call the same
// ``swap()`` primitive (htmx.js:3324 and 3350). ``swap()`` fires
// ``htmx:afterSwap`` on the swapped element BEFORE
// ``htmx:historyRestore`` is dispatched (htmx.js:1983 then :3329 /
// :3355). Our existing afterSwap handler already runs
// ``_runAutoInit(target, true)`` and the lazy-import branches for
// ``dashboard-map``, ``schedule-calendar``, and ``wysiwyg`` for
// ``id === 'main-content'``, so an extra historyRestore pass would
// double-mount them. The schedule symptom of the duplicate
// ``new Calendar(calendarEl)`` was image-4fed84df — TWO stacked
// FullCalendar instances inside ``#calendar``, the second one's
// move targets gone because the first's init had already removed
// ``#filter-btn-container`` and ``#schedule-view-tabs-container``.
//
// The original handler (added in e126d15) was based on the now-
// outdated belief that historyRestore fires WITHOUT an
// accompanying afterSwap. Source check on htmx 2.0.x proved
// otherwise. The sidebar fetch + toast cleanup historyRestore
// listeners further down the file are independent and stay.

// HTMX history restore — sidebar active-state recovery.
// `hx-history-elt` is intentionally scoped to `#main-content` (see the
// long comment in templates/layouts/base_dashboard.html) so the sidebar
// is NOT included in the per-history-entry snapshot. As a result the
// sidebar update path is one-way: forward navigations refresh it via
// `hx-select-oob="#sidebar"` on the boost response, but Back / Forward
// just restore the cached `#main-content` and leave the sidebar at the
// highlight of whatever page we last navigated TO.
//
// Concrete repro (the one the user reported):
//   1. Land on /office/jobs/                 → "Missions" highlighted
//   2. Click a customer name in the list     → /office/customers/<id>/
//                                              "Clients" highlighted
//   3. Press the browser Back button          → restores /office/jobs/
//                                              cached HTML, but sidebar
//                                              still shows "Clients".
//
// Re-fetch the restored URL with a vanilla `fetch()` (NOT `htmx.ajax`),
// parse the HTML, swap in the fresh `#sidebar` we find inside.
//
// Why the vanilla fetch instead of `htmx.ajax({ select: '#sidebar' })`:
//   `htmx.ajax` sets `HX-Request: true` on the request. Pages that opt
//   in to Pattern B (`{% extends base_office_template %}`) detect that
//   header and respond with ONLY the `#main-content` children — no
//   `#sidebar`. HTMX then runs `select: '#sidebar'` against that
//   sidebar-less fragment, finds nothing, and swaps an EMPTY string
//   into the live `#sidebar` with `swap: 'outerHTML'` — silently
//   deleting the entire navigation column. A plain `fetch()` carries
//   no `HX-Request` header, so the same view returns the full Pattern A
//   page and `#sidebar` is always present.
//
// Notes on the trade-off:
// - Costs one extra GET per Back / Forward (~50–200 ms for most office
//   list views). The page itself is restored from cache instantly, so
//   the user sees a brief "wrong sidebar" flicker that's still better
//   than the previous "wrong sidebar forever" behaviour.
// - We re-emit a synthetic `htmx:afterSwap` on the freshly inserted
//   `#sidebar` so the existing OOB-handler path picks it up and re-runs
//   Flowbite init for the dropdown collapses (Cleaners, Emails…).
// - On any failure (network, missing #sidebar in response, parse
//   error) we leave the existing sidebar in place — degraded "stale
//   active state" is fine; deleted sidebar is not.
document.body.addEventListener('htmx:historyRestore', function () {
    var url = window.location.pathname + window.location.search;
    fetch(url, {
        credentials: 'same-origin',
        headers: { 'Accept': 'text/html' },
    })
        .then(function (resp) {
            if (!resp.ok) throw new Error('http ' + resp.status);
            return resp.text();
        })
        .then(function (html) {
            var doc = new DOMParser().parseFromString(html, 'text/html');
            var fresh = doc.getElementById('sidebar');
            var live = document.getElementById('sidebar');
            if (!fresh || !live) return;
            live.replaceWith(fresh);
            if (window.htmx && typeof window.htmx.process === 'function') {
                window.htmx.process(fresh);
            }
            // Reuse the existing OOB-sidebar handler to re-init Flowbite
            // collapses on the new tree.
            fresh.dispatchEvent(new CustomEvent('htmx:afterSwap', {
                bubbles: true,
                detail: { target: fresh, elt: fresh },
            }));
        })
        .catch(function () {
            // Stale sidebar is acceptable — deleted sidebar is not.
        });
});

// Show Django messages delivered via HX-Trigger response header
document.body.addEventListener('showDjangoMessages', function(evt) {
    var messages = evt.detail && evt.detail.value;
    if (!messages || !Array.isArray(messages)) return;
    messages.forEach(function(msg, i) {
        setTimeout(function() {
            if (typeof window.showToast === 'function') {
                window.showToast(msg.message, msg.type);
            }
        }, i * 150);
    });
});

// Toast route-scoping: clear non-persistent toasts on navigation events.
// See docs/TOAST/TOAST_UX_RULES.md and docs/TOAST/TOAST_REFACTOR_PLAN.md.
// Toasts marked `data-persistent="1"` (opt-in via showToast(_, _, {
// persistAcrossNavigation: true })) survive these events.
function _clearToastsSafe() {
    if (typeof window.clearToasts === 'function') window.clearToasts();
}

// Flowbite tooltip/popover route-scoping. When the user hovers a button
// with [data-tooltip-target] then clicks it, the click triggers a
// boosted nav BEFORE Flowbite gets a `mouseleave` to hide the tooltip.
// HTMX then snapshots #main-content for its history cache WHILE the
// tooltip target div is still in its visible state (visible / opacity-100).
// Pressing Back restores that snapshot and the tooltip stays visible
// indefinitely as a "ghost" — no trigger element to dismiss it.
//
// Force-hide every `[role="tooltip"]` (and popover) on the same nav
// events that drive toast clearing, so the snapshot taken right after
// is clean and any snapshot-restore tail is also cleaned up.
function _hideFlowbiteTooltips() {
    document.querySelectorAll('[role="tooltip"]').forEach(function (el) {
        el.classList.add('invisible', 'opacity-0');
        el.classList.remove('visible', 'opacity-100');
    });
}

// hx-boost navigation start: a click/submit that's about to swap
// #main-content for a different URL. Filter on evt.detail.boosted so
// we don't nuke toasts on every partial XHR (e.g. soft-reload of a
// list, drawer GET, poller).
document.body.addEventListener('htmx:beforeRequest', function(evt) {
    if (evt.detail && evt.detail.boosted) {
        _clearToastsSafe();
        _hideFlowbiteTooltips();
    }
});

// HTMX history restore (Back/Forward on a boosted page) — fold into
// the existing handler defined just above so we don't re-bind twice.
document.body.addEventListener('htmx:historyRestore', function() {
    _clearToastsSafe();
    _hideFlowbiteTooltips();
});

// bfcache restore (native Back/Forward when the snapshot was kept) —
// the page comes back from a paused state, including any visible
// toast div in the body-level container.
window.addEventListener('pageshow', function(evt) {
    if (evt.persisted) {
        _clearToastsSafe();
        _hideFlowbiteTooltips();
    }
});

// After all swaps settle: fix sidebar + restore scroll (Flowbite drawer leaves overflow-hidden on body).
// Same double-fire caveat as htmx:afterSwap above — use `evt.target` (the
// actual dispatched element) rather than `evt.detail.target` so the sidebar
// OOB settle doesn't re-run the #main-content branch.
document.addEventListener('htmx:afterSettle', function(evt) {
    var settleId = evt.target && evt.target.id;
    if (settleId === 'main-content' || settleId === 'platform-content') {
        if (typeof window.reinitSidebar === 'function') window.reinitSidebar();
        document.body.classList.remove('overflow-hidden');
        document.body.style.removeProperty('overflow');
        document.documentElement.style.removeProperty('overflow');
        document.querySelectorAll('[drawer-backdrop]').forEach(function(el) { el.remove(); });
        // htmx.config.attributesToSettle (['class', 'style', 'width', 'height'])
        // runs a settle task that reverts id-matched inputs back to their
        // server-rendered class/style AFTER htmx:afterSwap. For intl-tel-input
        // fields that means `iti__tel-input`, `form-input-phone` and the
        // inline padding-left ITI set for separateDialCode are all stripped —
        // the .iti wrapper + dial-code badge stay, but the input's padding
        // resets to 10px, so the national number renders under the "+32" badge.
        // Re-asserting the styling here happens AFTER the settle task, so it
        // wins. No-op if no phone inputs exist or they're already styled.
        reapplyItiStyling(document.getElementById(settleId));
    }
});

// Initialize Flowbite + auto-init forms + lazy-load heavy modules on DOMContentLoaded.
// DOM element checks must be deferred because the bundle loads before body content.
document.addEventListener('DOMContentLoaded', function() {
    // Wipe any leftover /office/schedule entries from a previous session
    // BEFORE the user gets a chance to trigger a Back/Forward — see the
    // long comment on _purgeScheduleHistoryCache above.
    _purgeScheduleHistoryCache();
    // Pin the schedule history guard against the initial URL so
    // an immediate Back from this freshly-loaded page works.
    _setScheduleHistoryGuard();
    // Scope = `document` so persistent layout widgets (topbar drawer, bell,
    // user menu) get wired up alongside whatever landed in #main-content
    // on the initial navigation.
    reinitFlowbiteWidgets(document);
    initDarkMode();
    _runAutoInit();
    initEmailValidation();
    initDecimalInputs();
    initFormUrlSync();
    // Global confirm modal (window.showConfirmModal / hideConfirmModal).
    // Idempotent — bound once on the modal shell which lives in
    // base_office.html / base_platform.html and survives every
    // hx-boost swap. See utils/ui/confirm-modal.js for the contract.
    initConfirmModal();
    // Bound ONCE per SPA lifetime on document.body — survives every
    // hx-boost swap. See utils/ui/confirm-action.js for the contract.
    initConfirmAction();
    // Tiny declarative attrs: data-stop-propagation (direct-bind on
    // each matched node, re-scanned on hx swap), data-numeric-only
    // and data-print-email (delegated on body, bound once).
    initDeclarativeAttrs();
    // Open ONE WebSocket per session against /ws/org/. Per-page modules
    // subscribe to ``ws:<event_type>`` CustomEvents on document — see
    // ``utils/realtime.js`` and ``docs/REALTIME_WEBSOCKET.md``.
    // Idempotent — re-bootstrap is a no-op.
    bootstrapRealtime();
    // Hydrate the shared customer-picker translations from the
    // ``<script id="customer-picker-i18n" type="application/json">``
    // injected by base_office.html (Phase 21.D). Multiple downstream
    // modules read ``window.CUSTOMER_PICKER_I18N`` directly and need
    // it populated before any user interaction; doing it here matches
    // the timing of the original inline ``window.CUSTOMER_PICKER_I18N
    // = {…}`` assignment in base_office.html.
    hydrateCustomerPickerI18n();

    // Global: clear server-side validation errors when the user edits a field.
    // Works on any form using the .field-group / .has-error / .field-error pattern.
    document.addEventListener('input', function (e) {
        var field = e.target;
        if (field.tagName !== 'INPUT' && field.tagName !== 'TEXTAREA' && field.tagName !== 'SELECT') return;
        var fieldGroup = field.closest('.field-group.has-error');
        if (!fieldGroup) return;

        fieldGroup.classList.remove('has-error');
        field.classList.remove('border-red-500', 'dark:border-red-500');
        fieldGroup.querySelectorAll('.field-error, p.text-red-600').forEach(function (msg) { msg.remove(); });
    });

    // Global: money-input sanitizer for every input carrying the explicit
    // ``data-money-input`` marker (type="number" silently wiped the value
    // whenever the typed decimal separator didn't match the browser
    // locale). Accept comma OR dot, drop any other char, unify on a
    // single comma, preserve the caret.
    //
    // The marker is deliberately NOT ``inputmode="decimal"``: inputmode
    // is a keyboard hint, not a semantics contract — a future decimal
    // field that is NOT money (GPS coordinates, signed deltas…) must be
    // able to use the decimal keyboard without this sanitizer stripping
    // its minus sign or extra separators. ``DecimalTextInput`` (Django
    // widget) stamps the marker automatically; hand-written template
    // inputs add it explicitly.
    //
    // Server-side, every consumer normalises the comma back to a dot
    // (DecimalTextInput or apps.core.money.parse_money).
    document.addEventListener('input', function (e) {
        var el = e.target;
        if (!el || el.tagName !== 'INPUT') return;
        if (!el.hasAttribute('data-money-input')) return;
        sanitizeMoneyField(el);
    });

    // Heavy feature modules — lazy-loaded only on pages that need them.
    //
    // schedule-calendar.js pulls in the full FullCalendar bundle (~500
    // KiB) so it stays out of app.bundle.js. We ALSO need to trigger it
    // on ``htmx:afterSwap`` for the same reason as dashboard-map: a
    // boosted re-entry to /office/schedule swaps in fresh ``#calendar``
    // markup but the dynamic import promise from this DOMContentLoaded
    // run already resolved, so its top-level side effects don't fire a
    // second time. The afterSwap branch (search "schedule-calendar"
    // in this file) re-imports + re-invokes ``autoInitScheduleCalendar``,
    // which is idempotent thanks to the ``_calendar`` guard inside the
    // module.
    if (window.location.pathname.startsWith('/office/schedule') && document.getElementById('calendar')) {
        import(/* webpackChunkName: "schedule-calendar" */ "./pages/schedule-calendar")
            .then(function(mod) {
                if (typeof mod.autoInitScheduleCalendar === 'function') {
                    mod.autoInitScheduleCalendar();
                }
            });
    }
    // Team resource-timeline pulls in the FullCalendar + resource bundle,
    // so it is lazy-loaded only when the Team tab markup is present.
    if (window.location.pathname.startsWith('/office/schedule') && document.getElementById('team-calendar')) {
        import(/* webpackChunkName: "schedule-team" */ "./pages/schedule-team")
            .then(function(mod) {
                if (typeof mod.autoInitScheduleTeam === 'function') {
                    mod.autoInitScheduleTeam();
                }
            });
    }
    // Absences resource-timeline — same lazy-load contract as the schedule
    // views (FullCalendar bundle is code-split out of app.bundle.js). Only
    // present in the absences calendar view (the template skips the mount in
    // list view).
    if (window.location.pathname.startsWith('/office/cleaners/absences') && document.getElementById('abs-calendar')) {
        import(/* webpackChunkName: "absences-timeline" */ "./pages/absences-timeline")
            .then(function(mod) {
                if (typeof mod.autoInitAbsencesTimeline === 'function') {
                    mod.autoInitAbsencesTimeline();
                }
            });
    }
    if (
        document.getElementById("wysiwyg-example")
        || document.getElementById("composeModal")
        || document.getElementById("email-preview-pane")
    ) {
        import(/* webpackChunkName: "wysiwyg" */ "./utils/ui/wysiwyg");
    }
    if (document.getElementById("dash-map")) {
        import(/* webpackChunkName: "dashboard-map" */ "./pages/dashboard-map");
    }
    if (document.getElementById("report-content")) {
        import(/* webpackChunkName: "reports-charts" */ "./pages/reports-charts");
    }
});
// Lightweight modules -- always loaded
import { initDarkMode } from "./utils/ui/dark-mode";
import "./components/custom-select";
import "./utils/ui/table-column-reorder";

// Note: HTMX afterSwap handler (including dashboard-map lazy loading) is
// registered above alongside the boosted-navigation lifecycle handlers.
